import React, { useState } from "react";
import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { ChevronDown, Sparkles } from "lucide-react";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import { DEFAULT_PARSER_MODEL, PARSER_MODELS, parserModelLabel } from "./ParserModelMenu";

export const BRANDED_LOCALES = [
  { value: "", label: "Auto", hint: "Detected from the CV" },
  { value: "fr", label: "FR", hint: "Français" },
  { value: "nl", label: "NL", hint: "Nederlands" },
  { value: "en", label: "EN", hint: "English" },
];

export const localeHint = (value) =>
  BRANDED_LOCALES.find((locale) => locale.value === value)?.hint || BRANDED_LOCALES[0].hint;

const ghostSx = {
  textTransform: "none",
  fontSize: 12.5,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  px: 1.5,
  height: 30,
  flexShrink: 0,
  minWidth: 0,
  color: "hsl(150 30% 12%)",
  backgroundColor: "#ffffff",
  border: "1px solid hsl(140 8% 88%)",
  "& .MuiButton-startIcon": { mr: 0.5 },
  "&:hover": { backgroundColor: "hsl(140 10% 96%)" },
};

export const GenerateCvSplitButton = ({ onRun, label = "Generate CV", disabled = false }) => {
  const [anchor, setAnchor] = useState(null);

  const stop = (fn) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    fn(e);
  };

  return (
    <>
      <Box sx={{ display: "inline-flex", flexShrink: 0 }}>
        <Button
          size="small"
          disableRipple
          disabled={disabled}
          startIcon={<Sparkles size={14} />}
          onClick={stop(() => onRun(""))}
          sx={{ ...ghostSx, borderRadius: "calc(-2px + 0.75rem) 0 0 calc(-2px + 0.75rem)", borderRight: "none" }}
        >
          {label}
        </Button>
        <Button
          size="small"
          disableRipple
          disabled={disabled}
          aria-label="Choose the language"
          onClick={stop((e) => setAnchor(e.currentTarget))}
          sx={{ ...ghostSx, px: 0.5, borderRadius: "0 calc(-2px + 0.75rem) calc(-2px + 0.75rem) 0" }}
        >
          <ChevronDown size={13} />
        </Button>
      </Box>

      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={stop(() => setAnchor(null))}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography
          sx={{ px: 2, pt: 1, pb: 0.5, fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 55%)" }}
        >
          Generate in
        </Typography>
        {BRANDED_LOCALES.map((locale) => (
          <MenuItem
            key={locale.value}
            onClick={() => {
              setAnchor(null);
              onRun(locale.value);
            }}
            sx={{ fontSize: 13 }}
          >
            {locale.hint}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const PillRow = ({ options, value, onChange, getLabel }) => (
  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
    {options.map((option) => {
      const active = option.value === value;
      return (
        <Button
          key={option.value}
          size="small"
          disableRipple
          onClick={() => onChange(option.value)}
          sx={{
            textTransform: "none",
            fontSize: 12.5,
            px: 1.25,
            minWidth: 0,
            borderRadius: "8px",
            color: active ? "hsl(80 97% 72%)" : "hsl(150 30% 12%)",
            backgroundColor: active ? "hsl(150 38% 17%)" : "#ffffff",
            border: "1px solid hsl(140 8% 88%)",
            "&:hover": { backgroundColor: active ? "hsl(150 38% 22%)" : "hsl(140 10% 96%)" },
          }}
        >
          {getLabel(option)}
        </Button>
      );
    })}
  </Box>
);

export const RegenerateCvDialog = ({ open, onClose, onConfirm }) => {
  const [locale, setLocale] = useState("");
  const [model, setModel] = useState(DEFAULT_PARSER_MODEL);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={() => onConfirm({ locale, model })}
      title="Generate the Squarehub CV again"
      description="The current one is replaced. It is written by the AI and takes up to a minute."
      cancelLabel="Not now"
      confirmLabel="Generate again"
    >
      <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography sx={{ mb: 0.5, fontSize: 12, color: "hsl(150 10% 46%)" }}>Language</Typography>
          <PillRow options={BRANDED_LOCALES} value={locale} onChange={setLocale} getLabel={(o) => o.label} />
        </Box>
        <Box>
          <Typography sx={{ mb: 0.5, fontSize: 12, color: "hsl(150 10% 46%)" }}>AI model</Typography>
          <PillRow options={PARSER_MODELS} value={model} onChange={setModel} getLabel={(o) => parserModelLabel(o.value)} />
        </Box>
      </Box>
    </ConfirmDialog>
  );
};
